import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes.ts";
import { useUiStore } from "@/stores/ui.store.ts";
import { useUserStore } from "@/stores/user.store.ts";
import { getProfile } from "@/services/Auth.service.js";
import { useAuthStateStore } from "@/stores/auth-state.store.js";
import { useSidebarStore } from "@/stores/sidebar.store.js";
import { LICENSE_PRODUCTS } from "@/constants/licenseProducts.js";
import { redirectExternally } from "@/helpers/externalRedirect.js";

const EXTERNAL_REDIRECTS = {
  "api-docs": "https://www.dubrink.com/docs",
  "mkt-ppwr": "https://www.dubrink.com/ppwr",
  "mkt-eudr": "https://www.dubrink.com/eudr",
  "mkt-cbam": "https://www.dubrink.com/platform?utm_content=eu-cbam",
  "mkt-cbam-uk": "https://www.dubrink.com/uk-cbam",
};

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const getPostLoginRedirectPath = (toComponentName, userStore) => {
  const user = userStore.user;
  if (toComponentName !== "Azkaban" && user?.blocked) {
    return "/azkaban";
  }
  if (!user?.company) {
    return "/signup-completion";
  }
  if (
    !user?.company?.hasPlatformAccess &&
    !userStore.hasProductAccess() &&
    !["Updates", "UpdateDetail"].includes(toComponentName)
  ) {
    return "/waiting-list";
  }
  return;
};

const getJurisdictionRedirectPath = (to, user) => {
  const allowedJurisdictions = to.meta?.jurisdictions;
  // No juridisction restriction defined
  if (!allowedJurisdictions?.length) {
    return;
  }
  // Juridisction defined and allowed
  if (allowedJurisdictions.includes(user.company.jurisdiction)) {
    return;
  }
  // Fallback
  return "/home";
};

const MARKETING_REDIRECTS = {
  [LICENSE_PRODUCTS.PPWR]: "mkt-ppwr",
  [LICENSE_PRODUCTS.CBAM]: "mkt-cbam",
  [LICENSE_PRODUCTS.CBAM_MRV]: "mkt-cbam",
};

const getOverviewPath = (userStore) => {
  if (userStore.hasProductAccess(LICENSE_PRODUCTS.CBAM)) {
    return "/cbam/overview";
  }
  if (userStore.hasProductAccess(LICENSE_PRODUCTS.PPWR)) {
    return "/ppwr/overview";
  }
  if (userStore.hasProductAccess()) {
    return "/suppliers";
  }
  return null;
};

const getProductRedirectPath = (to, userStore) => {
  const products = to.meta?.products;
  if (!products || userStore.hasProductAccess(...products)) {
    return;
  }
  const product = products.find((item) => !userStore.hasProductAccess(item));
  return product ? `/home?redirect_to=${MARKETING_REDIRECTS[product]}` : "/home";
};

router.beforeEach(async (to, from) => {
  const uiStore = useUiStore();
  if (to.path !== from.path) {
    uiStore.clear();
  } else {
    // Query changes reuse the view's actions, but selections belong to the previous page.
    uiStore.selection = [];
    uiStore.showPopover = true;
  }
  const authStateStore = useAuthStateStore();
  try {
    if (to.meta.private === false) {
      return;
    }
    const userStore = useUserStore();
    const sidebarStore = useSidebarStore();
    if (!userStore.isLoggedIn) {
      const { data } = await getProfile();
      userStore.setUser(data);
      // The session is authenticated after profile fetch succeeds.
      authStateStore.markAuthenticatedSession();
    } else {
      // Keep this true across route changes in the same tab session.
      authStateStore.markAuthenticatedSession();
    }
    await sidebarStore.ensureLoaded();
    // Between pages => Validate if we're logged in
    const redirectPath = getPostLoginRedirectPath(to.name, userStore);
    if (redirectPath && redirectPath !== to.path) {
      return redirectPath;
    }
    const productRedirectPath = getProductRedirectPath(to, userStore);
    if (productRedirectPath && productRedirectPath !== to.fullPath) {
      return productRedirectPath;
    }
    const jurisdictionRedirectPath = getJurisdictionRedirectPath(to, userStore.user);
    if (jurisdictionRedirectPath && jurisdictionRedirectPath !== to.path) {
      return jurisdictionRedirectPath;
    }
    if (to.path === "/home") {
      if (EXTERNAL_REDIRECTS[to.query.redirect_to]) {
        redirectExternally(EXTERNAL_REDIRECTS[to.query.redirect_to]);
        return false;
      }
      const overviewPath = getOverviewPath(userStore);
      if (overviewPath) {
        return overviewPath;
      }
      return "/waiting-list";
    }
    if (to.path === "/waiting-list" && userStore.hasProductAccess()) {
      return "/";
    }

    return;
  } catch (error) {
    if (error?.status === 403 && error?.message === "ERROR.USER_BANNED") {
      if (to.path === "/azkaban") {
        return;
      }
      return {
        path: "/azkaban",
      };
    }
    if (error?.status === 401) {
      // After prior login in this tab, treat 401 as an expired session.
      if (authStateStore.hadAuthenticatedSession) {
        authStateStore.markSessionExpired(to.fullPath);
        return authStateStore.getSessionExpiredRoute();
      }
      // First-time unauthenticated users should be sent to login directly.
      return {
        path: "/login",
        query: {
          redirect_to: to.fullPath,
        },
      };
    }
    if (!error.status) {
      return {
        path: "/maintenance",
        query: {
          force: true,
        },
      };
    }
    const fullPath = to.fullPath;
    return {
      path: "/login",
      query: {
        redirect_to: fullPath,
      },
    };
  }
});

export default router;
